import React, { Component, createRef } from 'react';
import ReactDOM from 'react-dom';
import EmblaCarousel from 'embla-carousel';
import Autoplay from 'embla-carousel-autoplay';
import product1 from '../../assets/img/product/1.jpg';
import product2 from '../../assets/img/product/2.jpg';
import product3 from '../../assets/img/product/3.jpg';
import product4 from '../../assets/img/product/4.jpg';
import product5 from '../../assets/img/product/5.jpg';
import product6 from '../../assets/img/product/6.jpg';
import product7 from '../../assets/img/product/7.jpg';
import product8 from '../../assets/img/product/8.jpg';
import './LoginIllustration.scss';

const SLIDE_CAPTIONS = [
  {
    title: 'Built for niche markets',
    text: 'SOFC, LNG in maritime, CCUS in steel. Enki goes where BNEF, Rystad, and generic AI do not.',
  },
  {
    title: 'Every answer is cited and source-linked',
    text: 'Open the source before you act on it. The brief is defensible, not just confident-sounding.',
  },
  {
    title: 'Costs, trade-offs, and the next question to ask',
    text: 'The answer includes the numbers. Follow-up questions take you deeper without starting over.',
  },
  {
    title: 'Put a thesis to the test',
    text: 'State your position. Enki builds the case for and against it so you can decide with evidence.',
  },
  {
    title: 'Partnerships, launches, and market entries',
    text: 'Track who signed a deal, who launched a product, and who entered the market. Continuously updated.',
  },
  {
    title: 'Charts and tables, not just text',
    text: 'Enki pulls the visual data from its sources and puts it in the answer. No digging through reports.',
  },
  {
    title: 'Filter by time. The answer updates.',
    text: 'Narrow to the last quarter or the last year. The output restructures around the window you set.',
  },
  {
    title: 'The analysis is written for you',
    text: 'Enki reads the commercial signals and writes up what they mean for your market. Like having an analyst assemble the brief.',
  },
];

const AUTOPLAY_DELAY = 5000;
const AUTOPLAY_RESUME_DELAY = 15000;

export class LoginIllustration extends Component {
  emblaRef = createRef();
  lightboxRef = createRef();
  closeButtonRef = createRef();
  emblaApi = null;
  autoplayResumeTimer = null;
  lastFocusedElement = null;

  images = [
    product1,
    product2,
    product3,
    product4,
    product5,
    product6,
    product7,
    product8,
  ];

  componentDidMount() {
    this.emblaApi = EmblaCarousel(this.emblaRef.current, {
      loop: true,
      align: 'center',
    }, [Autoplay({ delay: AUTOPLAY_DELAY, stopOnInteraction: false })]);

    this.emblaApi.on('select', this.updateSelectedIndex);
    this.emblaApi.on('reInit', this.updateSelectedIndex);
    this.emblaApi.on('pointerDown', this.pauseAutoplay);
    this.updateSelectedIndex();
    document.addEventListener('keydown', this.onKeyDown);
  }

  componentWillUnmount() {
    clearTimeout(this.autoplayResumeTimer);
    document.removeEventListener('keydown', this.onKeyDown);
    document.body.style.overflow = '';
    this.emblaApi?.destroy();
  }

  /* Manual navigation pauses autoplay, which resumes once the carousel has been
     left alone for AUTOPLAY_RESUME_DELAY. */
  pauseAutoplay = () => {
    const autoplay = this.emblaApi?.plugins?.()?.autoplay;

    if (!autoplay) {
      return;
    }

    autoplay.stop();
    clearTimeout(this.autoplayResumeTimer);
    this.autoplayResumeTimer = setTimeout(() => {
      this.emblaApi?.plugins?.()?.autoplay?.play();
    }, AUTOPLAY_RESUME_DELAY);
  };

  state = {
    selectedIndex: 0,
    zoomedIndex: null,
    isNativeSize: false,
  };

  /* Lightbox: the screenshots are 3000x2500, so the carousel card always shows
     them heavily downscaled. Opening one full-screen is the only way to read
     the fine print without cropping the card. */
  openLightbox = (index) => {
    this.pauseAutoplay();
    this.lastFocusedElement = document.activeElement;
    document.body.style.overflow = 'hidden';
    this.setState({ zoomedIndex: index, isNativeSize: false }, () => {
      this.closeButtonRef.current?.focus();
    });
  };

  closeLightbox = () => {
    document.body.style.overflow = '';
    this.setState({ zoomedIndex: null, isNativeSize: false }, () => {
      this.lastFocusedElement?.focus?.();
      this.lastFocusedElement = null;
    });
  };

  /* Keeps Tab inside the dialog: without this it walks into the form behind
     the overlay, which is still in the tab order. */
  trapFocus = (event) => {
    if (event.key !== 'Tab') {
      return;
    }

    const focusable = this.lightboxRef.current?.querySelectorAll('button, [href], img[tabindex], [tabindex]:not([tabindex="-1"])');

    if (!focusable || !focusable.length) {
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  toggleNativeSize = () => {
    this.setState((state) => ({ isNativeSize: !state.isNativeSize }));
  };

  onKeyDown = (event) => {
    if (this.state.zoomedIndex === null) {
      return;
    }

    if (event.key === 'Escape') {
      this.closeLightbox();
      return;
    }

    this.trapFocus(event);
  };

  updateSelectedIndex = () => {
    this.setState({ selectedIndex: this.emblaApi.selectedScrollSnap() });
  };

  scrollTo = (index) => {
    this.pauseAutoplay();
    this.emblaApi?.scrollTo(index);
  };

  scrollPrev = () => {
    this.pauseAutoplay();
    this.emblaApi?.scrollPrev();
  };

  scrollNext = () => {
    this.pauseAutoplay();
    this.emblaApi?.scrollNext();
  };

  getSlideClass = (index) => {
    const { selectedIndex } = this.state;
    const total = this.images.length;
    const forward = (index - selectedIndex + total) % total;
    const distance = Math.min(forward, total - forward);

    if (distance === 0) {
      return 'embla__slide embla__slide--is-active';
    }

    if (distance === 1) {
      return forward === 1
        ? 'embla__slide embla__slide--is-next'
        : 'embla__slide embla__slide--is-prev';
    }

    return 'embla__slide embla__slide--is-far';
  };

  render() {
    const { variant = 'basic' } = this.props;
    const { selectedIndex, zoomedIndex, isNativeSize } = this.state;
    const isShowcase = variant === 'showcase';
    const caption = SLIDE_CAPTIONS[selectedIndex] || SLIDE_CAPTIONS[0];

    return (
      <div className={`embla-wrapper embla-wrapper--${variant}`}>
        <div className="embla-wrapper__stage">
          <div className="embla" ref={this.emblaRef}>
            <div className="embla__container">
              {this.images.map((img, index) => (
                <div className={isShowcase ? this.getSlideClass(index) : 'embla__slide'} key={index}>
                  <img
                    src={img}
                    alt={isShowcase && SLIDE_CAPTIONS[index] ? SLIDE_CAPTIONS[index].title : `Slide ${index}`}
                    className="embla__img"
                    loading={index === 0 ? 'eager' : 'lazy'}
                    onClick={isShowcase ? () => this.openLightbox(index) : undefined}
                  />
                </div>
              ))}
            </div>
          </div>

        </div>

        <div className="embla__dots">
          {this.images.map((_, index) => (
            <button
              type="button"
              key={index}
              onClick={() => this.scrollTo(index)}
              className={index === selectedIndex ? 'is-selected' : ''}
              aria-label={`Go to slide ${index + 1}`}
              aria-current={index === selectedIndex}
            />
          ))}
        </div>

        {isShowcase &&
          <div className="embla__caption" key={caption.title} aria-live="polite">
            <h3>{caption.title}</h3>
            <p>{caption.text}</p>
          </div>
        }

        {/* Rendered into document.body: inside the page's own position: fixed
            panel, the overlay's fixed positioning resolves against that panel
            instead of the viewport and leaves a strip of the app uncovered. */}
        {isShowcase && zoomedIndex !== null && ReactDOM.createPortal(
          <div
            className="embla__lightbox"
            ref={this.lightboxRef}
            role="dialog"
            aria-modal="true"
            aria-label={SLIDE_CAPTIONS[zoomedIndex] ? SLIDE_CAPTIONS[zoomedIndex].title : 'Product screenshot'}
            onClick={this.closeLightbox}
          >
            <button
              type="button"
              className="embla__lightbox__close"
              ref={this.closeButtonRef}
              onClick={this.closeLightbox}
              aria-label="Close"
            >
              <i className="material-icons">close</i>
            </button>

            <div
              className={isNativeSize ? 'embla__lightbox__frame is-native' : 'embla__lightbox__frame'}
              onClick={(event) => event.stopPropagation()}
            >
              <img
                className="embla__lightbox__img"
                src={this.images[zoomedIndex]}
                alt={SLIDE_CAPTIONS[zoomedIndex] ? SLIDE_CAPTIONS[zoomedIndex].title : `Slide ${zoomedIndex}`}
                onClick={this.toggleNativeSize}
              />
            </div>

            <p className="embla__lightbox__hint">
              {isNativeSize ? 'Click the image to fit it to the screen' : 'Click the image for full resolution'}
              {' · '}
              Esc to close
            </p>
          </div>,
          document.body
        )}
      </div>
    );
  }
}

export default LoginIllustration;
